import { types, getParent, type IAnyModelType } from 'mobx-state-tree';
import { modelReference } from '../refHelper';
import ObjectType, { type IObjectType } from './object_type';
import type { IMetaModel } from './index';
import type { IEventType } from './event_type';
import type { IMultiplePropagationConstraint } from './multiple_propagation_constraint';

/**
 * The Inheritance instance interface.
 *
 * HAND-DECLARED so the loose `modelReference` targets (`supertype`/`subtype`)
 * read as concrete `IObjectType`, and so sibling views see each other.
 */
export interface IInheritance {
  readonly id: string;
  readonly supertype: IObjectType;
  readonly subtype: IObjectType;
  readonly metamodel: IMetaModel;
  readonly displayString: string;
  readonly utf8DisplayString: string;
  readonly displayName: string;
  readonly subtypes: IObjectType[];
  readonly supertypes: IObjectType[];
  readonly previousObject: IObjectType;
  readonly nextObject: IObjectType;
  readonly specialisedEvents: IEventType[];
  readonly dependentMultiplePropagationConstraints: IMultiplePropagationConstraint[];
}

/**
 * A MERODE inheritance relation.
 */

const Inheritance = types
  .model('Inheritance', {
    id: types.identifier,
    supertype: modelReference(types.late((): IAnyModelType => ObjectType)),
    subtype: modelReference(types.late((): IAnyModelType => ObjectType)),
  })
  .views((self0) => {
    const self = self0 as unknown as IInheritance;
    return {
    // convenience method to get the metamodel
    get metamodel(): IMetaModel {
      // up 2-levels because 1 level up is the array of inheritances.
      return getParent(self0, 2);
    },
    get displayString() {
      return `${self.supertype.name} <- ${self.subtype.name}`;
    },
    get utf8DisplayString() {
      return self.displayString.replace('<-', '←');
    },
    get displayName() {
      return self.utf8DisplayString;
    },
    get subtypes() {
      return [self.subtype].concat(self.subtype.subtypes);
    },
    get supertypes() {
      return [self.supertype].concat(self.supertype.supertypes);
    },
    // Get the previous object when this inheritance appears in a path
    get previousObject() {
      return self.supertype;
    },
    // Get the next object when this dependency appears in a path
    get nextObject() {
      return self.subtype;
    },
    // Get the specialised events that have this inheritance as their inheritance
    get specialisedEvents() {
      return self.metamodel.eventTypes.filter((et) => et.specialised && et.inheritance === self);
    },
    // get the MPCs that depend on this inheritance
    get dependentMultiplePropagationConstraints() {
      return self.metamodel.multiplePropagationConstraints.filter((mpc) => mpc.dependsOnEdge(self));
    },
    };
  });

export default Inheritance;
